import { useMemo, useRef, useState } from 'react'

import { create, toBinary } from '@bufbuild/protobuf'
import { timestampDate } from '@bufbuild/protobuf/wkt'
import { Code, ConnectError } from '@connectrpc/connect'
import { UpdatePlantUnitRequestSchema } from '@ebalance/proto/bffuser/v1/occto_master_service_pb'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import {
  Alert,
  Flex,
  HStack,
  PrimaryButton,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'
import { FaPenToSquareRegular } from '@enechain/ecloud-icons'

import { AreaChangeConfirmDialog } from '~/components/common/AreaChangeConfirmDialog'
import { AuditPanel } from '~/components/common/AuditPanel'
import { CompactAlert } from '~/components/common/CompactAlert'
import { PageBody } from '~/components/common/PageBody'
import { PageLayout } from '~/components/common/PageLayout'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { PageSection } from '~/components/common/PageSection'
import { RegionLoading } from '~/components/common/RegionLoading'
import { UnsavedChangesDialog } from '~/components/common/UnsavedChangesDialog'
import { iconSize } from '~/constants/iconSize'
import { entitySourceOf, useEditModalForm } from '~/hooks/useEditModalForm'
import {
  convertListPlantMastersResponse,
  useListPlantMasters,
} from '~/hooks/useListPlantMasters'
import { useRevealOnChange } from '~/hooks/useRevealOnChange'
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard'
import {
  type PowerTypeChangeConfirmation,
  powerTypeChangeConfirmationOf,
} from '~/pages/AdminOcctoMaster/tabs/_common/models/powerTypeIntegrity'
import { UnitBasicView } from '~/pages/AdminPlantUnitDetail/components/UnitBasicView'
import { useGetPlantUnit } from '~/pages/AdminPlantUnitMaster/hooks/useGetPlantUnit'
import { useListGenerationSites } from '~/pages/AdminPlantUnitMaster/hooks/useListGenerationSites'
import { useListPlantUnits } from '~/pages/AdminPlantUnitMaster/hooks/useListPlantUnits'
import {
  useLivePowerTypeOverlapCheck,
  usePlantUnitFormSchema,
} from '~/pages/AdminPlantUnitMaster/hooks/usePowerTypeOverlapCheck'
import { useUpdatePlantUnit } from '~/pages/AdminPlantUnitMaster/hooks/useUpdatePlantUnit'
import { convertListGenerationSitesResponse } from '~/pages/AdminPlantUnitMaster/models/siteRows'
import { shouldClearPlantOnSiteChange } from '~/pages/AdminPlantUnitMaster/models/unitCandidates'
import {
  type PlantUnitFormInput,
  type PlantUnitFormValues,
  defaultPlantUnitFormInput,
  plantUnitAttributesFromValues,
  plantUnitRowToFormInput,
} from '~/pages/AdminPlantUnitMaster/models/unitForm'
import {
  type PlantUnitRow,
  convertListPlantUnitsResponse,
  convertPlantUnit,
} from '~/pages/AdminPlantUnitMaster/models/unitRows'
import {
  PLANT_UNIT_ERROR_KEY_PREFIX,
  PLANT_UNIT_SAVE_ERROR_KEYS,
  PLANT_UNIT_UNKNOWN_ERROR_KEY,
  resolvePlantUnitSaveError,
} from '~/pages/AdminPlantUnitMaster/models/unitServerError'
import { showSiteAreaMismatchToast } from '~/pages/AdminPlantUnitMaster/models/unitToast'
import { routePaths } from '~/routes/routePaths'

/**
 * 電源ユニットの詳細 (`/admin/plant-unit-master/:id`)。基本情報を閲覧し、「編集」→
 * 「キャンセル」/「保存」で更新する。所属 BG と作成・更新の記録は読み取り専用。
 *
 * 未保存のまま画面を離れる時は確認を出す。
 */
export const AdminPlantUnitDetailPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const getQuery = useGetPlantUnit(id)
  const { data, isPending, isError, error } = getQuery
  const sitesQuery = useListGenerationSites()
  const plantsQuery = useListPlantMasters()
  const unitsQuery = useListPlantUnits()
  const { mutateAsync, isPending: isSaving } = useUpdatePlantUnit()

  const unit =
    data?.plantUnit !== undefined ? convertPlantUnit(data.plantUnit) : undefined
  const siteRows =
    sitesQuery.data !== undefined
      ? convertListGenerationSitesResponse(sitesQuery.data)
      : []
  const plantRows =
    plantsQuery.data !== undefined
      ? convertListPlantMastersResponse(plantsQuery.data)
      : []

  const [isEditing, setEditing] = useState(false)
  const allUnits = useMemo(
    () =>
      unitsQuery.data !== undefined
        ? convertListPlantUnitsResponse(unitsQuery.data)
        : [],
    [unitsQuery.data],
  )
  const schema = usePlantUnitFormSchema({
    units: allUnits,
    plants: plantRows,
    selfId: unit?.id,
    // 系統コードを変えずに電源種別だけを変える保存は、入力中のエラーにせず保存時の確認 (Q9) に任せる
    original:
      isEditing && unit !== undefined
        ? { plantMasterId: unit.plantMasterId ?? '', powerType: unit.powerType }
        : undefined,
  })

  // 電源種別の変更の確認: 「同じ系統コードの他のユニットも変わる」で止まったら確認を出し、「変更」で
  // 見せた (id, 更新時刻) を付けて送り直す (確認の後に入力を変えたら、確認からやり直す)
  const lastPowerTypeConfirmation = useRef<
    PowerTypeChangeConfirmation | undefined
  >(undefined)

  const {
    confirmation,
    confirm,
    cancelConfirmation,
    isSubmitting,
    control,
    errors,
    serverError,
    handleClose,
    handleSave,
    setValue,
    getValues,
    trigger,
    isDirty,
    loadStatus,
  } = useEditModalForm<
    PlantUnitRow,
    PlantUnitFormInput,
    PlantUnitFormValues,
    PowerTypeChangeConfirmation
  >({
    // 「編集」を押した時の取得結果で form を埋める
    source: entitySourceOf(
      getQuery,
      (response) =>
        response.plantUnit !== undefined
          ? convertPlantUnit(response.plantUnit)
          : undefined,
      { enabled: isEditing },
    ),
    toFormValues: plantUnitRowToFormInput,
    schema,
    defaultValues: defaultPlantUnitFormInput(),
    confirmationOf: (err) => {
      const parsed = powerTypeChangeConfirmationOf(t, err)
      if (parsed !== undefined) lastPowerTypeConfirmation.current = parsed
      return parsed
    },
    prepareSubmit: (values, { entity, expectedUpdatedAt }) => {
      const request = {
        ...plantUnitAttributesFromValues(values),
        id: entity.id,
        generationSiteId: values.siteId,
        // 編集を始めた時の版 (proto Timestamp のまま。JS Date を経由するとマイクロ秒が落ちる)
        expectedUpdatedAt,
      }
      return {
        // 確認の後に入力を変えたら、同じ確認では送らない (確認からやり直す)
        identity: toBinary(
          UpdatePlantUnitRequestSchema,
          create(UpdatePlantUnitRequestSchema, request),
        ),
        send: async (powerTypeChangeConfirmed) => {
          const response = await mutateAsync({
            ...request,
            powerTypeChangeConfirmedUnits: powerTypeChangeConfirmed
              ? (lastPowerTypeConfirmation.current?.confirmedUnits ?? [])
              : [],
          })
          showSiteAreaMismatchToast(t, response.siteAreaMismatch)
        },
      }
    },
    onClose: () => setEditing(false),
    customErrorKeys: PLANT_UNIT_SAVE_ERROR_KEYS,
    unknownErrorKey: PLANT_UNIT_UNKNOWN_ERROR_KEY,
    resolveDetailedError: (err, tt, values) =>
      resolvePlantUnitSaveError(err, tt, {
        unitName: values.unitName,
        siteId: values.siteId,
        siteName:
          siteRows.find((site) => site.id === values.siteId)?.siteName ?? '',
      }),
  })

  useLivePowerTypeOverlapCheck(control, trigger)

  // 欄に付かないエラー (上部の Alert) は、下までスクロールして保存した時も見える位置へ寄せる
  const alertRef = useRevealOnChange<HTMLDivElement>(serverError)
  // 発電所を変えたら、新しい発電所のエリアに合わない系統コードの選択を外す
  const handleSiteChanged = (siteId: string | undefined): void => {
    const { plantMasterId } = getValues()
    if (plantMasterId === '') return
    if (
      shouldClearPlantOnSiteChange(
        siteRows.find((site) => site.id === siteId)?.areaCode,
        plantRows.find((row) => row.id === plantMasterId)?.areaCode,
      )
    ) {
      setValue('plantMasterId', '', { shouldDirty: true })
    }
  }

  const guard = useUnsavedGuard({ isDirty: isEditing && isDirty })

  const startEdit = (): void => {
    if (isSaving || unit === undefined) return
    setEditing(true)
  }

  const cancelEdit = (): void => {
    if (isSaving) return
    handleClose()
  }

  const siteName = siteRows.find(
    (site) => site.id === unit?.generationSiteId,
  )?.siteName
  const plant = plantRows.find((row) => row.id === unit?.plantMasterId)
  const plantLabel =
    plant === undefined ? undefined : `${plant.plantCode} ${plant.plantName}`

  const breadcrumbItems = [
    {
      label: t('page:adminPlantUnitMaster.breadcrumb.admin'),
      to: routePaths.adminHub.path,
    },
    {
      label: t('common:route.adminPlantUnitMaster'),
      to: routePaths.adminPlantUnitMaster.path,
    },
    { label: t('common:route.adminPlantUnitDetail'), bold: true },
  ]

  const loadErrorMessage = isError
    ? error instanceof ConnectError && error.code === Code.NotFound
      ? t('page:adminPlantUnitMaster.unit.detail.notFound')
      : t(`${PLANT_UNIT_ERROR_KEY_PREFIX}.loadFailed`)
    : undefined

  return (
    <PageLayout breadcrumbItems={breadcrumbItems}>
      <PageBody>
        {loadErrorMessage !== undefined ? (
          <Alert description={loadErrorMessage} status="error" />
        ) : isPending || unit === undefined ? (
          <RegionLoading minHeight="120px" isLoading />
        ) : (
          <PageScrollArea>
            <Flex flexDirection="column" gap={6}>
              <PageSection
                actions={
                  isEditing ? (
                    <HStack gap={2}>
                      <TertiaryButton
                        disabled={isSaving}
                        onClick={cancelEdit}
                        size="sm"
                      >
                        {t('common:action.cancel')}
                      </TertiaryButton>
                      <PrimaryButton
                        disabled={isSaving || loadStatus !== 'ready'}
                        loading={isSaving}
                        onClick={handleSave}
                        size="sm"
                      >
                        {t('common:action.save')}
                      </PrimaryButton>
                    </HStack>
                  ) : (
                    <TertiaryButton onClick={startEdit} size="sm">
                      <FaPenToSquareRegular
                        boxSize={iconSize.labeledButton}
                        aria-hidden="true"
                      />
                      {t('common:action.edit')}
                    </TertiaryButton>
                  )
                }
                title={t('page:adminPlantUnitMaster.unit.detail.basic')}
              >
                <Flex flexDirection="column" gap={3}>
                  {isEditing && serverError !== undefined && (
                    <div ref={alertRef}>
                      <CompactAlert
                        role="alert"
                        status="error"
                        title={serverError}
                      />
                    </div>
                  )}
                  <RegionLoading
                    isLoading={isEditing && loadStatus === 'loading'}
                  >
                    <UnitBasicView
                      editing={
                        isEditing && loadStatus === 'ready'
                          ? {
                              control,
                              errors,
                              disabled: isSaving,
                              sites: siteRows,
                              plants: plantRows,
                              onSiteChanged: handleSiteChanged,
                            }
                          : undefined
                      }
                      plantLabel={plantLabel}
                      siteName={siteName}
                      unit={unit}
                    />
                  </RegionLoading>
                  {isEditing && isDirty && (
                    <Text textStyle="caption" color="Text.midEmphasis">
                      {t('page:adminPlantUnitMaster.unit.detail.unsavedNote')}
                    </Text>
                  )}
                  <AuditPanel
                    createdAt={
                      unit.createdAt === undefined
                        ? undefined
                        : timestampDate(unit.createdAt)
                    }
                    createdBy={unit.createdBy}
                    updatedAt={
                      unit.updatedAt === undefined
                        ? undefined
                        : timestampDate(unit.updatedAt)
                    }
                    updatedBy={unit.updatedBy}
                  />
                </Flex>
              </PageSection>
            </Flex>
          </PageScrollArea>
        )}
      </PageBody>
      {confirmation !== undefined && (
        <AreaChangeConfirmDialog
          confirmLabel={t(
            'page:adminOcctoMaster.powerTypeIntegrity.change.confirmButton',
          )}
          confirmation={confirmation}
          isLoading={isSaving || isSubmitting}
          onCancel={cancelConfirmation}
          onConfirm={confirm}
        />
      )}
      <UnsavedChangesDialog
        isOpen={guard.isBlocked}
        onCancel={guard.cancel}
        onConfirm={guard.proceed}
      />
    </PageLayout>
  )
}
